import React from "react";
import { connect } from "react-redux";

import { Dispatch, bindActionCreators } from "redux";
import nameof from "ts-nameof.macro";

import { TranslatedText } from "@plex/culture-react";
import { Dialog, SelectionMode, DataTableDef, Form, CancelLink, Text } from "@plex/react-components";

import { isLocalizable } from "infrastructure/localization";
import { INotification } from "infrastructure/notification";
import { IValidationIssue } from "infrastructure/validation";
import { Banner } from "ui/app";
import { DialogContainer } from "ui/app/navigation";
import { useLocalizedValues } from "ui/hooks";
import commonStyles from "ui/screens/translation-managers/shared/styles/Translations.module.scss";
import { displayMessages } from "ui/screens/translation-managers/translation-managers.localization";

import { plexTranslationUploadActions } from "./PlexTranslationUpload.actions";
import styles from "./PlexTranslationUpload.module.scss";
import { IPlexTranslationUploadValidationIssue, IPlexTranslationUploadError, IPlexTranslationUploadState } from "./PlexTranslationUpload.state";

export interface IPlexTranslationUploadResult {
  error?: IPlexTranslationUploadError;
  notifications: INotification[];
  resetState: () => void;
}

export const PlexTranslationUploadResult: React.FC<IPlexTranslationUploadResult> = ({ error, notifications, resetState }) => {
  const localizedValues = useLocalizedValues(() => {
    return {
      messageId: displayMessages.property("plexTranslationUploadResultItemMessageId"),
      translatedText: displayMessages.property("plexTranslationUploadResultItemTranslatedText"),
      locale: displayMessages.property("plexTranslationUploadResultItemLocale"),
      industry: displayMessages.property("plexTranslationUploadResultItemIndustry"),
      validationResult: displayMessages.property("plexTranslationUploadResultItemResult"),
      validationResultSectionHeader: displayMessages.heading("plexTranslationUploadValidationResults"),
      dialogTitle: displayMessages.title("plexTranslationsUploadValidationResult")
    };
  });

  const getKey = (keyPart: string) => {
    return `plexTranslationUploadResultGrid-${keyPart}`;
  };

  const getMessage = (issue: IValidationIssue) => {
    if (isLocalizable(issue.message)) {
      return <TranslatedText messageId={issue.message.messageId.value} context={issue.message.tokens} />;
    } else {
      return issue.message;
    }
  };

  return (
    <DialogContainer
      onHidden={resetState}
      render={dialogProps => {
        return (
          <Dialog {...dialogProps} title={localizedValues.dialogTitle}>
            <Dialog.Body>
              <Banner messages={notifications} messageClassName={commonStyles.dialogBanner} />
              {error && (
                <Form>
                  <Form.Body>
                    <Form.Section title={localizedValues.validationResultSectionHeader}>
                      <Form.Row>
                        {error.validationIssues ? (
                          <DataTableDef<IPlexTranslationUploadValidationIssue>
                            data={error.validationIssues}
                            keySelector={row => row.uniqueId}
                            selected={[]}
                            selectionMode={SelectionMode.none}
                          >
                            <DataTableDef.Column<IPlexTranslationUploadValidationIssue>
                              id={getKey(nameof(localizedValues.messageId))}
                              key="1"
                              title={localizedValues.messageId}
                              sortable
                            >
                              {row => <p>{row.messageId}</p>}
                            </DataTableDef.Column>
                            <DataTableDef.Column<IPlexTranslationUploadValidationIssue> id={getKey(nameof(localizedValues.locale))} key="2" title={localizedValues.locale} sortable>
                              {row => <p>{row.locale}</p>}
                            </DataTableDef.Column>
                            <DataTableDef.Column<IPlexTranslationUploadValidationIssue>
                              id={getKey(nameof(localizedValues.industry))}
                              key="3"
                              title={localizedValues.industry}
                              sortable
                            >
                              {row => <p>{row.industry}</p>}
                            </DataTableDef.Column>
                            <DataTableDef.Column<IPlexTranslationUploadValidationIssue>
                              id={getKey(nameof(localizedValues.translatedText))}
                              key="4"
                              title={localizedValues.translatedText}
                              sortable
                            >
                              {row => <p>{row.messageTranslation}</p>}
                            </DataTableDef.Column>
                            <DataTableDef.Column<IPlexTranslationUploadValidationIssue>
                              id={getKey(nameof(localizedValues.validationResult))}
                              key="5"
                              title={localizedValues.validationResult}
                              sortable
                            >
                              {row => <p>{getMessage(row.validationIssue)}</p>}
                            </DataTableDef.Column>
                          </DataTableDef>
                        ) : (
                          <Form.FieldPair labelText={localizedValues.validationResult} className={styles.formResultField}>
                            <Text text={error.text} />
                          </Form.FieldPair>
                        )}
                      </Form.Row>
                    </Form.Section>
                  </Form.Body>
                </Form>
              )}
            </Dialog.Body>
            <Dialog.Footer>
              <CancelLink onClick={dialogProps.onHide}></CancelLink>
            </Dialog.Footer>
          </Dialog>
        );
      }}
    />
  );
};

export default connect(
  (state: { plexTranslationsUpload: IPlexTranslationUploadState }) => {
    return {
      error: state.plexTranslationsUpload.error,
      notifications: state.plexTranslationsUpload.notifications
    };
  },
  (dispatch: Dispatch) => {
    return bindActionCreators(
      {
        resetState: plexTranslationUploadActions.resetUploadingPlexTranslation
      },
      dispatch
    );
  }
)(PlexTranslationUploadResult);
